/* ============================================================================
 * ThemePerf · Global (for Ella 6.x)
 * Put this file in assets/custom.js
 * Goal: reduce LCP/Speed Index/main-thread work without changing core UX.
 * Debug: ?perfdebug=1
 * ========================================================================== */
;(function () {
  'use strict';

  if (window.__ThemePerfGlobalLoaded) return;
  window.__ThemePerfGlobalLoaded = true;

  var win = window;
  var doc = document;
  var html = doc.documentElement;
  var body = doc.body;

  var raf = win.requestAnimationFrame || function (cb) { return setTimeout(cb, 16); };
  var ric = win.requestIdleCallback || function (cb) {
    return setTimeout(function () {
      cb({ timeRemaining: function () { return 0; } });
    }, 250);
  };

  var toArr = function (nl) { return Array.prototype.slice.call(nl || []); };
  var ua = navigator.userAgent || '';
  var isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(ua);

  var conn = navigator.connection || navigator.webkitConnection || navigator.mozConnection || {};
  var saveData = !!conn.saveData;
  var slowNet = /^(slow-2g|2g)$/i.test(conn.effectiveType || '');
  var ROOT_MARGIN = (isMobile || saveData || slowNet) ? '560px 0px' : '880px 0px';

  var CONFIG = {
    debug: /\bperfdebug=1\b/.test(location.search),
    rootMargin: ROOT_MARGIN,
    scrollingClass: 'is-scrolling',

    heroSelectors: [
      '[data-hero-lcp] img, [data-hero-lcp] picture > img',
      '.slide-show img[loading="eager"]',
      '.slide-show picture > img',
      '.halo-section .banner img',
      '.hero-media img',
      'main img'
    ],

    videoSelector: [
      'video.js-video',
      'video.video',
      'video.background_video_mb',
      'video.slide-mb',
      'video.slide-mobile',
      'video[data-src]'
    ].join(','),

    lazyImgSelector: 'img[data-src], img[data-srcset]',
    lazyBgSelector: '[data-bg]',

    thirdPartyOnLoad: [
      // 'https://example-app-cdn.com/widget.js'
    ],
    thirdPartyOnIdle: [
      // 'https://example-analytics.com/analytics.js'
    ],

    preconnectHosts: [
      'https://cdn.shopify.com',
      'https://fonts.gstatic.com',
      'https://fonts.googleapis.com'
    ],

    longListThreshold: 12,
    enableLcpPreload: true,
    firstHydrateBudget: isMobile ? 6 : 10
  };

  function log() {
    if (!CONFIG.debug) return;
    try { console.log.apply(console, arguments); } catch (_e) {}
  }

  function throttle(fn, wait) {
    var last = 0;
    return function () {
      var now = Date.now();
      if (now - last >= wait) {
        last = now;
        fn.apply(this, arguments);
      }
    };
  }

  function once(fn) {
    var done = false;
    return function () {
      if (done) return;
      done = true;
      return fn.apply(this, arguments);
    };
  }

  function decodeImage(img) {
    if (!img || !img.decode) return Promise.resolve();
    try {
      return img.decode().catch(function () {});
    } catch (_e) {
      return Promise.resolve();
    }
  }

  function inViewport(el) {
    if (!el || !el.getBoundingClientRect) return false;
    var r = el.getBoundingClientRect();
    return r.top < (win.innerHeight || 800) && r.bottom > 0 && r.width * r.height > 0;
  }

  var loadedScript = Object.create(null);
  function loadScript(url, attrs) {
    if (!url || loadedScript[url]) return;
    loadedScript[url] = 1;

    var s = doc.createElement('script');
    s.src = url;
    s.defer = true;
    s.crossOrigin = 'anonymous';

    if (attrs) {
      for (var k in attrs) {
        if (Object.prototype.hasOwnProperty.call(attrs, k)) {
          try { s.setAttribute(k, attrs[k]); } catch (_e) {}
        }
      }
    }

    (doc.head || html).appendChild(s);
  }

  function addPreconnect(url) {
    if (!url) return;
    if (doc.querySelector('link[rel="preconnect"][href="' + url + '"]')) return;

    var l = doc.createElement('link');
    l.rel = 'preconnect';
    l.href = url;
    l.crossOrigin = 'anonymous';
    (doc.head || html).appendChild(l);
  }

  var markScrolling = (function () {
    var active = false;
    var timer = null;

    function add() {
      if (active) return;
      active = true;
      raf(function () {
        html.classList.add(CONFIG.scrollingClass);
        if (body) body.classList.add(CONFIG.scrollingClass);
      });
    }

    function remove() {
      clearTimeout(timer);
      timer = setTimeout(function () {
        active = false;
        html.classList.remove(CONFIG.scrollingClass);
        if (body) body.classList.remove(CONFIG.scrollingClass);
      }, 180);
    }

    var onScroll = throttle(function () {
      add();
      remove();
    }, isMobile ? 140 : 110);

    win.addEventListener('scroll', onScroll, { passive: true });
    win.addEventListener('wheel', onScroll, { passive: true });
    win.addEventListener('touchmove', onScroll, { passive: true });

    return { add: add, remove: remove };
  })();

  var pauseCandidates = new Set();
  var pauseOutOfView = throttle(function () {
    pauseCandidates.forEach(function (v) {
      if (!v || v.paused) return;
      if (!inViewport(v)) {
        try { v.pause(); } catch (_e) {}
      }
    });
  }, 180);

  win.addEventListener('scroll', pauseOutOfView, { passive: true });
  doc.addEventListener('visibilitychange', function () {
    if (doc.hidden) pauseOutOfView();
  });

  function primeVideo(v) {
    if (!v || v.__tpPrimed) return;
    v.__tpPrimed = true;

    try {
      if (!v.hasAttribute('playsinline')) v.setAttribute('playsinline', '');
      if (v.preload !== 'none') v.preload = 'none';
      if (isMobile && v.hasAttribute('autoplay') && !v.hasAttribute('muted')) v.setAttribute('muted', '');

      if (v.getAttribute('src') && !v.dataset.src) {
        v.dataset.src = v.getAttribute('src');
        v.removeAttribute('src');
      }

      toArr(v.querySelectorAll('source[src]')).forEach(function (s) {
        if (!s.getAttribute('data-src')) {
          s.setAttribute('data-src', s.getAttribute('src'));
          s.removeAttribute('src');
        }
      });

      pauseCandidates.add(v);
    } catch (_e) {}
  }

  function hydrateVideo(v) {
    if (!v || v.__tpHydrated) return;
    v.__tpHydrated = true;

    try {
      var ds = v.getAttribute('data-src');
      if (ds && !v.getAttribute('src')) v.setAttribute('src', ds);

      toArr(v.querySelectorAll('source[data-src]')).forEach(function (s) {
        if (!s.getAttribute('src')) s.setAttribute('src', s.getAttribute('data-src'));
      });

      v.load();

      if (v.hasAttribute('autoplay')) {
        var p = v.play && v.play();
        if (p && p.catch) p.catch(function () {});
      }

      log('[perf] video hydrated:', v);
    } catch (_e) {}
  }

  function hydrateImg(img) {
    if (!img || img.__tpHydrated) return;
    img.__tpHydrated = true;

    try {
      if (img.dataset.src) {
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
      }
      if (img.dataset.srcset) {
        img.srcset = img.dataset.srcset;
        img.removeAttribute('data-srcset');
      }
      decodeImage(img);
      log('[perf] img hydrated:', img.currentSrc || img.src);
    } catch (_e) {}
  }

  function hydrateBg(el) {
    if (!el || el.__tpHydrated) return;
    el.__tpHydrated = true;

    try {
      var url = el.getAttribute('data-bg');
      if (url) {
        el.style.backgroundImage = 'url("' + url + '")';
        el.removeAttribute('data-bg');
      }
      log('[perf] bg hydrated');
    } catch (_e) {}
  }

  var io = ('IntersectionObserver' in win)
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;

          var el = entry.target;
          io.unobserve(el);

          if (el.tagName === 'VIDEO') hydrateVideo(el);
          else if (el.tagName === 'IMG') hydrateImg(el);
          else if (el.hasAttribute('data-bg')) hydrateBg(el);
        });
      }, { rootMargin: CONFIG.rootMargin })
    : null;

  function observe(el) {
    if (!el || el.dataset.lazy === 'off') return;

    if (!io) {
      if (el.tagName === 'VIDEO') hydrateVideo(el);
      else if (el.tagName === 'IMG') hydrateImg(el);
      else if (el.hasAttribute('data-bg')) hydrateBg(el);
      return;
    }

    io.observe(el);
  }

  function registerVideos(root) {
    toArr(root.querySelectorAll(CONFIG.videoSelector)).forEach(function (v, idx) {
      primeVideo(v);
      if (v.__tpObserved || v.preload !== 'none') return;
      v.__tpObserved = true;

      if (idx < CONFIG.firstHydrateBudget && inViewport(v)) {
        hydrateVideo(v);
      } else {
        observe(v);
      }
    });
  }

  function registerImages(root) {
    toArr(root.querySelectorAll(CONFIG.lazyImgSelector)).forEach(function (img) {
      if (img.__tpObserved) return;
      img.__tpObserved = true;
      observe(img);
    });
  }

  function registerBackgrounds(root) {
    toArr(root.querySelectorAll(CONFIG.lazyBgSelector)).forEach(function (el) {
      if (el.__tpObserved) return;
      el.__tpObserved = true;
      observe(el);
    });
  }

  function registerAll(scope) {
    var root = scope || doc;
    registerVideos(root);
    registerImages(root);
    registerBackgrounds(root);
  }

  function enhanceLCP() {
    var hero = null;

    for (var i = 0; i < CONFIG.heroSelectors.length; i++) {
      hero = doc.querySelector(CONFIG.heroSelectors[i]);
      if (hero) break;
    }

    if (!hero) {
      var imgs = toArr(doc.images || []);
      var best = null;
      var bestArea = 0;

      imgs.forEach(function (im) {
        var r = im.getBoundingClientRect();
        if (r.top >= 0 && r.top < (win.innerHeight || 800)) {
          var area = r.width * r.height;
          if (area > bestArea) {
            bestArea = area;
            best = im;
          }
        }
      });

      hero = best;
    }

    if (!hero) return;

    decodeImage(hero);

    if (!CONFIG.enableLcpPreload) return;
    if (!hero.closest('[data-lcp-preload]')) return;

    var href = hero.currentSrc || hero.src || '';
    if (!href) return;
    if (doc.querySelector('link[rel="preload"][as="image"][href="' + href + '"]')) return;

    try {
      var link = doc.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = href;
      (doc.head || html).appendChild(link);
    } catch (_e) {}
  }

  function tuneImages(scope) {
    var root = scope || doc;

    ric(function () {
      toArr(root.querySelectorAll('img[loading="lazy"]')).forEach(function (img) {
        if (!img.hasAttribute('decoding')) img.setAttribute('decoding', 'async');

        var inList = !!img.closest('.productListing, .product-item, .collection, .grid');
        if (inList && !img.hasAttribute('fetchpriority')) {
          img.setAttribute('fetchpriority', 'low');
        }
      });
    });
  }

  function prefetchVariantOnHover() {
    var timers = new WeakMap();

    doc.addEventListener('pointerenter', function (e) {
      var lbl = e.target && e.target.closest && e.target.closest('.swatch-label');
      if (!lbl) return;

      var url = lbl.getAttribute('data-variant-img');
      if (!url) return;

      if (timers.has(lbl)) clearTimeout(timers.get(lbl));

      var t = setTimeout(function () {
        var im = new Image();
        im.decoding = 'async';
        im.loading = 'eager';
        im.src = url;
      }, 80);

      timers.set(lbl, t);
    }, { passive: true, capture: true });

    doc.addEventListener('pointerleave', function (e) {
      var lbl = e.target && e.target.closest && e.target.closest('.swatch-label');
      if (!lbl || !timers.has(lbl)) return;
      clearTimeout(timers.get(lbl));
      timers.delete(lbl);
    }, { passive: true, capture: true });
  }

  function markInViewCards(scope) {
    if (!('IntersectionObserver' in win)) return;

    var root = scope || doc;
    var items = root.querySelectorAll('.product-item');
    if (!items.length) return;

    var io2 = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('tp-inview');
        io2.unobserve(entry.target);
      });
    }, { rootMargin: '100px 0px', threshold: 0.02 });

    toArr(items).forEach(function (el) { io2.observe(el); });
  }

  function enhanceLongLists(scope) {
    var root = scope || doc;
    var supports = ('contentVisibility' in html.style) || ('content-visibility' in html.style);
    if (!supports) return;

    toArr(root.querySelectorAll('#main-collection-product-grid, #CollectionProductGrid, .productListing')).forEach(function (list) {
      if (list.__tpCvApplied) return;

      var count = (list.querySelectorAll('.product-item') || []).length;
      if (count >= CONFIG.longListThreshold) {
        list.classList.add('tp-cv-section');
        list.__tpCvApplied = true;
      }
    });
  }

  function fixMobileDotsGap() {
    var APPLY_MAX_MS = 8000;
    var start = Date.now();

    function apply() {
      if (win.innerWidth > 749) return;

      try {
        toArr(doc.querySelectorAll('.slick-dots, .slider-dots')).forEach(function (el) {
          el.style.display = 'none';
          el.style.height = '0px';
          el.style.margin = '0px';
          el.style.padding = '0px';
          el.style.lineHeight = '0';
        });

        toArr(doc.querySelectorAll('.slick-dotted, .products-carousel.slick-dotted, .slideshow.slick-dotted')).forEach(function (el) {
          el.style.marginBottom = '0px';
        });
      } catch (_e) {}
    }

    [0, 120, 360, 1000, 2000, 4000, 6500].forEach(function (t) {
      setTimeout(apply, t);
    });

    if ('MutationObserver' in win) {
      var mo = new MutationObserver(function (muts) {
        if (Date.now() - start > APPLY_MAX_MS) {
          mo.disconnect();
          return;
        }

        for (var i = 0; i < muts.length; i++) {
          var m = muts[i];
          if (m.type === 'attributes' && m.attributeName === 'class') { apply(); return; }
          if (m.addedNodes && m.addedNodes.length) { apply(); return; }
        }
      });

      mo.observe(html, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['class']
      });

      setTimeout(function () {
        try { mo.disconnect(); } catch (_e) {}
      }, APPLY_MAX_MS);
    }

    win.addEventListener('resize', throttle(apply, 200));
  }

  function watchDOM() {
    if (!('MutationObserver' in win)) return;

    var pending = [];
    var scheduled = false;

    function flush() {
      scheduled = false;

      if (!pending.length) return;

      var roots = pending.splice(0, pending.length);
      roots.forEach(function (root) {
        if (!root || root.nodeType !== 1) return;
        registerAll(root);
        tuneImages(root);
        markInViewCards(root);
        enhanceLongLists(root);
      });
    }

    function schedule(root) {
      pending.push(root);
      if (scheduled) return;
      scheduled = true;
      raf(flush);
    }

    var mo = new MutationObserver(function (muts) {
      muts.forEach(function (m) {
        toArr(m.addedNodes).forEach(function (n) {
          if (!n || n.nodeType !== 1) return;
          schedule(n);
        });
      });
    });

    mo.observe(html, { childList: true, subtree: true });
  }

  function bindShopifyEditorEvents() {
    doc.addEventListener('shopify:section:load', function (e) {
      var container = e && e.target;
      if (!container) return;
      registerAll(container);
      tuneImages(container);
      markInViewCards(container);
      enhanceLongLists(container);
    });

    doc.addEventListener('shopify:section:select', function (e) {
      var container = e && e.target;
      if (!container) return;
      registerAll(container);
    });
  }

  function initThirdPartyLoaders() {
    var afterFirstInteraction = once(function () {
      CONFIG.thirdPartyOnLoad.forEach(function (u) { loadScript(u); });
    });

    win.addEventListener('scroll', afterFirstInteraction, { passive: true, once: true });
    win.addEventListener('pointerdown', afterFirstInteraction, { passive: true, once: true });
    win.addEventListener('keydown', afterFirstInteraction, { passive: true, once: true });

    ric(function () {
      CONFIG.thirdPartyOnIdle.forEach(function (u) { loadScript(u); });
    });
  }

  function init() {
    CONFIG.preconnectHosts.forEach(addPreconnect);

    registerAll(doc);
    enhanceLCP();

    fixMobileDotsGap();

    tuneImages(doc);
    prefetchVariantOnHover();
    markInViewCards(doc);
    enhanceLongLists(doc);

    watchDOM();
    bindShopifyEditorEvents();
    initThirdPartyLoaders();

    log('[perf] ThemePerf Global initialized');
  }

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
